import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment/moment';
import { Button, Dropdown, Form } from 'semantic-ui-react';
import styled from 'styled-components';
import * as companyActions from '../actions/commons';
import * as usageAction from '../actions/usage';
import cm from '../helpers/commons';
import UserSikdaeGrandDeductDetailModal from '../components/UserSikdaeGrandDeductDetailModal';
import exportedJsonData from '../components/UserSikdaeGrandDeductDetailModal/sidkaeGrandDeduct.json';
import { DateForm, LoadingBar, Pagination, SearchInputBox, SikdaeGroup, TableForm } from '../components/Commons';
import storage from '../helpers/storage';
import toastr from '../helpers/toastr';

const InputWrapper = styled.div`
  & .search-input button {
    display: none;
  }
`;

const pageSizeOptions = [
  { text: '10씩 보기', value: 10 },
  { text: '25씩 보기', value: 25 },
  { text: '50씩 보기', value: 50 },
  { text: '100씩 보기', value: 100 },
  { text: '200씩 보기', value: 200 }
];
const defaultColumns = [
  { header: '날짜', name: 'usingDate', textAlign: 'left' },
  { header: '로그인 ID', name: 'signId', textAlign: 'left' },
  { header: '이름', name: 'name', textAlign: 'left' },
  { header: '사번', name: 'comIdNum', textAlign: 'left' },
  { header: '휴대전화번호', name: 'cellphone', textAlign: 'left' },
  { header: '이메일', name: 'email', textAlign: 'left' },
  { header: '직위', name: 'position', textAlign: 'left' },
  { header: '직책', name: 'rankPosition', textAlign: 'left' },
  { header: '식대그룹', name: 'mealGroupName', textAlign: 'left' },
  { header: '총 지급금액', name: 'grantAmount', textAlign: 'right' },
  { header: '총 사용금액', name: 'usedAmount', textAlign: 'right' },
  { header: '총 소멸금액', name: 'expiredAmount', textAlign: 'right' }
];
const generateColumns = (values) => {
  const columns = cm.tableHeaderList(defaultColumns, 6, null, 'notMaxWidth');

  const thead = columns.map((item) => ({ data: item.header, ...item }));
  const tbody = values.map((value) => {
    const division = cm.trTdDivision(value.orgCode);

    const row = columns.map((item) => {
      let data = value[item.name] || '-';
      let fontColor = '';

      if (item.header.includes('부서')) {
        const idx = Number(item.header.replace('부서', '')) - 1;
        data = division[idx] || '-';
      } else if (item.name === 'grantAmount' || item.name === 'usedAmount' || item.name === 'expiredAmount') {
        data = value[item.name].toLocaleString() || 0;
        fontColor = (item.name === 'usedAmount' || item.name === 'expiredAmount') && 'red';
      }
      return { data, ...item, fontColor };
    });
    return { row };
  });
  return { thead, tbody: values.length ? tbody : undefined };
};

const UserSikdaeGrandDeductHistory = ({
  companyAPI,
  usageAPI,
  date,
  dateCheck,
  group,
  keyword,
  sikdaeGrandDeductHistory,
  sikdaeGrandDeductTotal
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchDate, setSearchDate] = useState({ startDate: '-', endDate: '-' });
  const [isOpen, setIsOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isDisable, setIsDisable] = useState(false);

  useEffect(() => {
    usageAPI.cleanSikdaeGrandDeduct();
    companyAPI.sikdaeGroupList();
  }, []);

  const { grandDeductHistory, grandDeductTotalHistory, grandDeductTotal } = useMemo(() => {
    const { totalRow = 0, content = [] } = sikdaeGrandDeductHistory.data || {};
    const { totalExpiredAmount, totalGrantAmount, totalUsedAmount } = sikdaeGrandDeductTotal.data || {};
    const histories = content.slice((page - 1) * pageSize, page * pageSize);
    return {
      grandDeductHistory: histories,
      grandDeductTotal: { totalExpiredAmount, totalGrantAmount, totalUsedAmount, totalRow },
      grandDeductTotalHistory: content
    };
  }, [page, pageSize, sikdaeGrandDeductHistory, sikdaeGrandDeductTotal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const params = {
        startDate: date
          ? moment(date.sDate).format('YYYY-MM-DD')
          : moment()
              .startOf('month')
              .format('YYYY-MM-DD'),
        endDate: date ? moment(date.eDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
        mealGroupIdx: group.value || null,
        keyword: keyword.value || null
      };
      if (moment(params.endDate).diff(params.startDate, 'day') >= 31) {
        alert(`날짜 검색 조건 최대 범위는 31일까지 입니다.`);
        return;
      }
      await usageAPI.getGrandDeductHistory(params);
      await usageAPI.getGrandDeductTotal(params);
    } catch (e) {
      setIsLoading(false);
      const { response } = e;
      if (response && response.data) {
        toastr('top-right', 'error', response.data.message);
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleEntSubmit = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  const handleOnOffModal = () => setIsOpen(!isOpen);
  const handleRowClick = (_, index) => {
    const { userId, usingDate } = grandDeductHistory[index];
    if (userId) {
      const [startDate, endDate] = usingDate.split(' ~ ');
      const params = {
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD')
      };
      setSearchDate(params);
      usageAPI.getGrandDeductDetail(userId, params);
      handleOnOffModal();
    }
  };

  const handleExcelDownload = async () => {
    if (grandDeductTotalHistory.length) {
      const depth = storage.get('depth');
      const fileName = `사용자별 식대 지급/차감 내역_${moment().valueOf()}`;
      const newCols = [];
      for (let i = 1; i <= depth; i += 1) {
        newCols.push({ rowNum: 2, value: `부서${i}` });
      }
      const newRows = grandDeductTotalHistory.map((item) => {
        const division = cm.trTdDivision(item.orgCode);
        return [
          '',
          item.usingDate,
          item.signId,
          item.name,
          item.comIdNum,
          item.cellphone,
          item.email,
          ...division,
          item.position,
          item.rankPosition,
          item.mealGroupName,
          item.grantAmount,
          item.usedAmount,
          item.expiredAmount
        ];
      });
      const apiExcelDatas = {
        0: { startRowNum: 3, newRows, insertColNum: 8, newCols }
      };
      await handleFileExport(exportedJsonData, apiExcelDatas, fileName);
      setTimeout(() => {
        setIsDisable(() => false);
      }, 1000);
    } else {
      alert(`엑셀 다운로드는 조회된 데이터 기반으로 다운로드됩니다.\n먼저 데이터를 조회해 주세요.`);
    }
  };

  const handlePageChange = (value) => setPage(value);
  const handlePageSizeChange = (_, target) => {
    setPage(1);
    setPageSize(target.value || 25);
  };

  const tableData = useMemo(() => {
    const { thead, tbody } = generateColumns(grandDeductHistory);
    const { totalExpiredAmount, totalGrantAmount, totalUsedAmount } = grandDeductTotal;

    const sub = [
      { data: '합계', colSpan: thead.length - 3, textAlign: 'left' },
      { data: (totalGrantAmount || 0).toLocaleString(), textAlign: 'right' },
      { data: (totalUsedAmount || 0).toLocaleString(), textAlign: 'right' },
      { data: (totalExpiredAmount || 0).toLocaleString(), textAlign: 'right' }
    ];
    return { header: { main: thead, sub }, body: { main: tbody, message: '식대 지급/차감 내역이 존재하지 않습니다' } };
  }, [grandDeductHistory]);

  return (
    <main className="usgl">
      {isLoading ? <LoadingBar /> : null}
      <div className="top-btn button">
        <Button inverted color="green" onClick={handleExcelDownload} disabled={isDisable}>
          엑셀 다운로드
        </Button>
      </div>
      {/* Search Form */}
      <Form className="top-form" onSubmit={handleSubmit}>
        <Form.Group style={{ alignItems: 'center', margin: '0 0 1em' }}>
          <span style={{ padding: '4px 0px', fontWeight: 700 }}>기간 간편 검색</span>
          <DateForm maxDays={31} />
        </Form.Group>
        <Form.Group style={{ alignItems: 'center', margin: '0 0 1em' }}>
          <span style={{ padding: '4px 0px', fontWeight: 700 }}>식대 그룹</span>
          <SikdaeGroup style={{ height: '32px' }} />
          <InputWrapper>
            <SearchInputBox
              placeHolder="이름, 사원번호, 로그인ID, 휴대전화번호로 검색하세요."
              styles={{ width: '375px', marginRight: '7px' }}
              handleEntSubmit={handleEntSubmit}
            />
          </InputWrapper>
          <Button color="black" content="조회" inverted style={{ maxWidth: '100px', height: '32px' }} />
        </Form.Group>
      </Form>
      {/* Table */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0px' }}>
        <span style={{ fontWeight: 700 }}>검색 결과 : {(grandDeductTotal.totalRow || 0).toLocaleString()}건</span>
        <Dropdown selection defaultValue={25} options={pageSizeOptions} onChange={handlePageSizeChange} />
      </div>
      <div className="table-box">
        <TableForm selectable="true" tabledata={tableData} togglestatus onClick={handleRowClick} />
      </div>
      <div className="table-paging">
        <Pagination
          key={pageSize}
          page={page}
          pageRow={pageSize}
          pageBlock={10}
          totalCount={grandDeductTotal.totalRow}
          pageClick={handlePageChange}
        />
      </div>
      <UserSikdaeGrandDeductDetailModal open={isOpen} close={handleOnOffModal} searchDate={searchDate} />
    </main>
  );
};
export default connect(
  (state) => ({
    date: state.commons.date,
    dateCheck: state.commons.dateCheck,
    division: state.commons.division,
    group: state.commons.group,
    keyword: state.commons.text,
    //
    sikdaeGrandDeductHistory: state.usage.userSikdaeGrandDeductHistory || {},
    sikdaeGrandDeductTotal: state.usage.userSikdaeGrandDeductTotal || {}
  }),
  (dispatch) => ({
    companyAPI: bindActionCreators({ sikdaeGroupList: companyActions.SikdaeGroupList }, dispatch),
    usageAPI: bindActionCreators(
      {
        getGrandDeductHistory: usageAction.UserSikdaeGrandDeductHistory,
        getGrandDeductTotal: usageAction.UserSikdaeGrandDeductTotal,
        getGrandDeductDetail: usageAction.UserSikdaeGrandDeductDetail,
        cleanSikdaeGrandDeduct: usageAction.CleanSikdaeGrandDeduct
      },
      dispatch
    )
  })
)(UserSikdaeGrandDeductHistory);
